/** Superadmin → Safety: Toolbox Talk library — every uploaded video, re-usable in any slot with one tap. */
import { useState } from "react";
import { Library, Pencil, Check, Trash2, Video } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

const SLOT_LABEL = { default: "Default", mon: "Mon", tue: "Tue", wed: "Wed", thu: "Thu", fri: "Fri", sat: "Sat", sun: "Sun" };
export const slotLabel = (k, slots) => SLOT_LABEL[k] || (k.startsWith("job:") ? `Job: ${slots?.[k]?.project_name || k.slice(4)}` : k);

export default function SafetyLibrary({ items, slots, onReload, onSettings }) {
  const [renaming, setRenaming] = useState(null);
  const [title, setTitle] = useState("");
  const [preview, setPreview] = useState(null);

  const rename = async (id) => {
    try { await api.put(`/safety/library/${id}`, { title }); setRenaming(null); onReload(); toast.success("Renamed"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not rename"); }
  };
  const remove = async (id) => {
    try { await api.delete(`/safety/library/${id}`); onReload(); toast.success("Removed from library"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not delete"); }
  };
  const assignDefault = async (id) => {
    try { const { data } = await api.post(`/safety/library/${id}/assign`, { slot: "default" }); onSettings(data); onReload(); toast.success("Now the default video"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not assign"); }
  };

  return (
    <div className="rounded-xl border border-gray-200 p-4 space-y-3" data-testid="safety-library-section">
      <div className="flex items-center gap-3">
        <Library className="h-4 w-4 text-[#1a3fd1]" />
        <div>
          <div className="text-sm font-semibold text-gray-900 dark:text-white">Toolbox Talk library</div>
          <p className="text-xs text-gray-500">Every video you've uploaded. Re-use one with "Use as default", or pick it from the "From library" menu on any day/job slot above.</p>
        </div>
      </div>
      {!items.length && <p className="text-xs text-gray-500">Nothing in the library yet.</p>}
      <div className="divide-y divide-gray-200" data-testid="safety-library-list">
        {items.map((v, i) => (
          <div key={v.video_id} className="py-2" data-testid={`safety-library-item-${i}`}>
            <div className="flex flex-wrap items-center gap-2">
              <div className="mr-auto min-w-0">
                {renaming === v.video_id ? (
                  <div className="flex items-center gap-1">
                    <input value={title} onChange={(e) => setTitle(e.target.value)} onKeyDown={(e) => e.key === "Enter" && rename(v.video_id)} className="rounded-md border border-gray-300 bg-transparent px-2 py-0.5 text-sm text-gray-900 dark:text-white" autoFocus data-testid={`safety-library-item-${i}-title-input`} />
                    <button onClick={() => rename(v.video_id)} className="text-emerald-600" aria-label="Save title"><Check className="h-4 w-4" /></button>
                  </div>
                ) : (
                  <div className="flex items-center gap-1.5 text-sm font-medium text-gray-900 dark:text-white">
                    <span className="truncate" data-testid={`safety-library-item-${i}-title`}>{v.title}</span>
                    <button onClick={() => { setRenaming(v.video_id); setTitle(v.title); }} className="text-gray-400 hover:text-[#1a3fd1]" aria-label="Rename" data-testid={`safety-library-item-${i}-rename`}><Pencil className="h-3 w-3" /></button>
                  </div>
                )}
                <div className="text-[11px] text-gray-500">
                  {v.name} · {(v.size / 1048576).toFixed(1)} MB · {new Date(v.uploaded_at).toLocaleDateString()} by {v.uploaded_by}
                  {v.used_in?.length ? <span className="ml-1 rounded-full bg-emerald-100 px-1.5 py-0.5 font-semibold text-emerald-800 dark:bg-emerald-500/20 dark:text-emerald-200">in use: {v.used_in.map((k) => slotLabel(k, slots)).join(", ")}</span> : <span className="ml-1 text-gray-400">not in use</span>}
                </div>
              </div>
              <button onClick={() => setPreview(preview === v.video_id ? null : v.video_id)} className="inline-flex items-center gap-1 rounded-md border border-gray-300 px-2 py-1 text-xs font-semibold text-gray-700 dark:text-white/80" data-testid={`safety-library-item-${i}-preview`}><Video className="h-3.5 w-3.5" /> {preview === v.video_id ? "Hide" : "Preview"}</button>
              <button onClick={() => assignDefault(v.video_id)} className="rounded-md bg-[#1a3fd1] px-2 py-1 text-xs font-semibold text-white" data-testid={`safety-library-item-${i}-use`}>Use as default</button>
              <button onClick={() => remove(v.video_id)} disabled={!!v.used_in?.length} title={v.used_in?.length ? "Remove it from its slots first" : "Delete from library"} className="rounded-md border border-red-300 p-1 text-red-600 disabled:opacity-40" aria-label="Delete" data-testid={`safety-library-item-${i}-delete`}><Trash2 className="h-3.5 w-3.5" /></button>
            </div>
            {preview === v.video_id && <video controls playsInline preload="metadata" className="mt-2 aspect-video w-full max-w-md rounded-lg bg-black" src={`${process.env.REACT_APP_BACKEND_URL}/api/safety/library/${v.video_id}/stream`} />}
          </div>
        ))}
      </div>
    </div>
  );
}
